import React, { useCallback } from 'react';
import { Box, Stack, Tooltip, Typography } from '@mui/material';
import { useFormContext } from 'react-hook-form';
import type { TranslationPath } from 'c-translation';
import { Translate, useCommonTranslation } from 'c-translation';
import { Checkbox, CollapsibleCard, ControlledFormInput } from 'c-components';
import type { Skin } from 'c-entity-types';
import { darkThemeOptions, readableOnDark } from 'c-lib';
import type { ColorSectionItem } from './SkinColorSection';
import { SkinColorGrid } from './SkinColorSection';

type Props = {
    submitButton: React.ReactNode;
    title: TranslationPath;
};

// each manual dark colour alongside the regular colour it replaces in dark mode
const darkColors: (ColorSectionItem & { lightKey: keyof Skin })[] = [
    {
        key: 'dark_primary_color',
        lightKey: 'primary_color',
        label: 'Modules.Admin.SkinEdit.form.primaryColorLabel',
    },
    {
        key: 'dark_secondary_color',
        lightKey: 'secondary_color',
        label: 'Modules.Admin.SkinEdit.form.secondaryColorLabel',
    },
    {
        key: 'dark_third_color',
        lightKey: 'third_color',
        label: 'Modules.Admin.SkinEdit.form.tertiaryColorLabel',
    },
    {
        key: 'dark_fourth_color',
        lightKey: 'fourth_color',
        label: 'Modules.Admin.SkinEdit.form.quaternaryColorLabel',
    },
    {
        key: 'dark_fifth_color',
        lightKey: 'fifth_color',
        label: 'Modules.Admin.SkinEdit.form.quinaryColorLabel',
    },
    {
        key: 'dark_sixth_color',
        lightKey: 'sixth_color',
        label: 'Modules.Admin.SkinEdit.form.senaryColorLabel',
    },
];

const SkinDarkModeSection: React.FC<Props> = ({ submitButton, title }) => {
    const t = useCommonTranslation();
    const { watch, getValues, setValue } = useFormContext<Skin>();
    // null until saved, which counts as automatic
    const automatic = watch('dark_mode_auto') !== false;
    const values = watch();

    const onAutomaticChange = useCallback(
        (checked: boolean, onChange: (value: boolean) => void) => {
            if (!checked) {
                // start any unset manual colours from what automatic mode was showing
                darkColors.forEach(({ key, lightKey }) => {
                    const lightColor = getValues(lightKey) as string;
                    if (!getValues(key) && lightColor) {
                        setValue(key, readableOnDark(lightColor), { shouldDirty: true });
                    }
                });
            }
            onChange(checked);
        },
        [getValues, setValue],
    );

    return (
        <CollapsibleCard title={title}>
            <Box mb={2} pl={4}>
                {submitButton}
            </Box>
            <Box px={4} pb={2}>
                <ControlledFormInput
                    name="dark_mode_auto"
                    render={({ field }) => (
                        <Checkbox
                            {...field}
                            value={automatic}
                            isBoolean
                            onChange={checked => onAutomaticChange(checked, field.onChange)}
                            label={t('Modules.Admin.SkinEdit.form.darkModeAutoLabel')}
                        />
                    )}
                />
                <Typography variant="body2" color="text.secondary">
                    <Translate
                        path={
                            automatic
                                ? 'Modules.Admin.SkinEdit.form.darkModeAutoDescription'
                                : 'Modules.Admin.SkinEdit.form.darkModeManualDescription'
                        }
                    />
                </Typography>
            </Box>
            {automatic ? (
                <Stack
                    direction="row"
                    flexWrap="wrap"
                    gap={2}
                    px={4}
                    py={2}
                    mx={4}
                    mb={2}
                    borderRadius={1}
                    bgcolor={darkThemeOptions.palette.background.paper}
                >
                    {darkColors.map(({ lightKey, label }) => {
                        const color = readableOnDark(values[lightKey] as string);
                        return color ? (
                            <Tooltip key={lightKey} title={<Translate path={label} />}>
                                <Box
                                    width={40}
                                    height={40}
                                    borderRadius={1}
                                    bgcolor={color}
                                    aria-label={t(label)}
                                />
                            </Tooltip>
                        ) : null;
                    })}
                </Stack>
            ) : (
                <SkinColorGrid items={darkColors} />
            )}
        </CollapsibleCard>
    );
};

export default SkinDarkModeSection;
